import { getTestOverrides } from '@wix/cli-test-overrides';
import type { WorkerOptions, RequestInit } from 'miniflare';
import {
  Miniflare,
  fetch as miniflareFetch,
  Request as MiniflareRequest,
  NoOpLog,
} from 'miniflare';
import type { Logger, Plugin } from 'vite';
import { fetchModule } from 'vite';
import getPort from 'get-port';
import { relative } from 'node:path';
import { minimatch } from 'minimatch';
import { pathToFileURL } from 'node:url';
import { getViteServerUrl, pipeFromWeb, toURL, toWeb } from './utils.js';

const FETCH_MODULE_PATHNAME = '/__vite_fetch_module';

const supportedNodeBuiltInExternals = [
  'node:assert',
  'node:async_hooks',
  'node:buffer',
  'node:crypto',
  'node:diagnostics_channel',
  'node:events',
  'node:path',
  'node:process',
  'node:stream',
  'node:string_decoder',
  'node:util',
  'module',
];

export function viteMiniflarePlugin(options: {
  entry: string;
  workerEntry: string;
  invalidateGlobPatterns?: string[];
  customLogger?: Logger;
}): Plugin {
  let miniflare: Miniflare | undefined;
  const { invalidateGlobPatterns } = options;
  return {
    name: 'miniflare',
    config() {
      return {
        appType: 'custom',
        environments: {
          ssr: {
            resolve: {
              noExternal: true,
              external: supportedNodeBuiltInExternals,
            },
          },
        },
      };
    },
    hotUpdate(options) {
      const entriesToInvalidate = [
        ...this.environment.moduleGraph.fileToModulesMap.entries(),
      ].filter(([filepath]) =>
        invalidateGlobPatterns?.some((pattern) =>
          minimatch(
            relative(options.server.config.root, filepath).replace(/^\//, ''),
            pattern
          )
        )
      );

      for (const [_, modules] of entriesToInvalidate) {
        for (const module of modules.values()) {
          this.environment.moduleGraph.invalidateModule(module);
        }
      }
    },
    async configureServer(this, viteDevServer) {
      return async () => {
        viteDevServer.middlewares.use(FETCH_MODULE_PATHNAME, (req, res) => {
          const url = toURL(req);
          const id = url.searchParams.get('id');
          const importer = url.searchParams.get('importer') ?? undefined;

          if (id) {
            res.setHeader('Cache-Control', 'no-store');
            res.setHeader('Content-Type', 'application/json');
            fetchModule(viteDevServer.environments.ssr, id, importer)
              .then((ssrModule) => res.end(JSON.stringify(ssrModule)))
              .catch((error: unknown) => {
                res.statusCode = 500;

                const errorMessage =
                  error instanceof Error ? error.message : 'unexpected error';

                res.end(errorMessage);
              });
          } else {
            res.writeHead(400, { 'Content-Type': 'text/plain' });
            res.end('Invalid request');
          }
        });

        viteDevServer.middlewares.use(async (req, res) => {
          if (!miniflare) {
            const testOverrides = getTestOverrides();

            const outboundService = testOverrides.miniflareRequestsRewrites
              ? createOutboundService(testOverrides.miniflareRequestsRewrites)
              : undefined;

            miniflare = new Miniflare({
              port: 0,
              log: new NoOpLog(),
              handleRuntimeStdio: (stdout, stderr) => {
                if (options.customLogger) {
                  const logger = options.customLogger;

                  stdout.on('data', (x: Buffer) => logger.info(x.toString()));
                  stderr.on('data', (x: Buffer) => logger.error(x.toString()));
                }
              },
              inspectorPort: await getPort({ port: 9229 }),
              modules: [
                {
                  type: 'ESModule',
                  path: options.workerEntry,
                },
              ],
              outboundService,
              modulesRoot: '/',
              unsafeEvalBinding: '__VITE_UNSAFE_EVAL',
              compatibilityDate: '2025-03-27',
              compatibilityFlags: ['nodejs_compat'],
              bindings: {
                __VITE_ROOT: viteDevServer.config.root,
                __VITE_FETCH_MODULE_PATHNAME: FETCH_MODULE_PATHNAME,
                __VITE_RUNTIME_EXECUTE_URL: pathToFileURL(options.entry)
                  .pathname,
                __VITE_DEV_SERVER_URL: getViteServerUrl(viteDevServer),
                __VITE_NODE_BUILTINS: supportedNodeBuiltInExternals,
              },
            });
          }

          await miniflare.ready;

          miniflare
            .dispatchFetch(toWeb(req))
            .then((miniflareResponse) => pipeFromWeb(miniflareResponse, res))
            .catch((error: unknown) => {
              res.writeHead(500);
              res.end();

              throw error;
            });
        });
      };
    },

    async buildEnd() {
      if (miniflare) {
        await miniflare.dispose();
        miniflare = undefined;
      }
    },
  };
}

interface RewriteRule {
  from: string;
  to: string;
}

function createOutboundService(requestsRewrites: RewriteRule[]) {
  return async function outboundService(request) {
    const rewriteRule = requestsRewrites.find((rewrite) =>
      request.url.startsWith(rewrite.from)
    );

    if (rewriteRule) {
      const rewrittenRequest = new MiniflareRequest(
        request.url.replace(rewriteRule.from, rewriteRule.to),
        request as RequestInit
      );
      return miniflareFetch(rewrittenRequest);
    }

    return miniflareFetch(request, {
      // in the code inside miniflare fetch impl **that handles websocket connections**
      // they assume that requestInit (the second arg) is always defined when they read
      // the `dispatcher` property, so we need to provide a dummy value here
      dispatcher: undefined,
    });
  } satisfies WorkerOptions['outboundService'];
}
